import type { StyledComponent } from "@emotion/styled";
import styled from "@emotion/styled";
import type { IconName } from "@fortawesome/fontawesome-svg-core";
import {
  Button as MuiButton,
  IconButton as MuiIconButton,
  type ButtonTypeMap,
  type IconButtonProps,
  type ButtonProps as MuiButtonProps,
} from "@mui/material";

import { shade } from "@skilitics/designer-theme";

import { Icon } from "../icons/Icon";
import { BUTTON_SMALL_HEIGHT } from "../utilities";
export interface IconButtonPropsOverride extends Omit<IconButtonProps, "css"> {
  secondary?: boolean;
  icon?: IconName;
  ["data-test"]: string;
}
const UnstyledIconButton = ({ children, icon, ref, ...props }: IconButtonPropsOverride) => (
  <MuiIconButton {...props} ref={ref}>
    {icon ? <Icon icon={icon} /> : children}
  </MuiIconButton>
);

export const IconButton = styled(UnstyledIconButton)`
  font-size: 1rem;
  height: ${BUTTON_SMALL_HEIGHT}px;
  width: ${BUTTON_SMALL_HEIGHT}px;
  ${({ theme, color }) => !color && `color: ${theme.generalTextIcon}`};
`;

export type ButtonProps = MuiButtonProps<ButtonTypeMap["defaultComponent"], { target?: HTMLAnchorElement["target"] }>;

type ButtonPropsOverride = ButtonProps & {
  secondary?: boolean;
};

export const Button: StyledComponent<ButtonProps> = styled(MuiButton)`
  border-radius: ${({ theme }) => theme.borderRadius}px;
`;

export const PrimaryButton = (props: ButtonProps) => <Button color="primary" variant="contained" {...props} />;

export const OutlineButton = (props: ButtonProps) => <Button variant="outlined" {...props} />;

export const DestructiveButton: StyledComponent<ButtonPropsOverride> = styled((props: ButtonPropsOverride) => (
  <Button color="primary" variant="contained" {...props} />
))`
  color: ${({ theme }) => theme.onError};
  background-color: ${({ theme }) => theme.error};
  &:hover {
    background-color: ${({ theme }) => shade(theme.error, 0.1)};
  }
`;

export const RegressiveButton: StyledComponent<ButtonProps> = DestructiveButton;

// TODO: Expand ButtonGroup to allow for various alignment of buttons.
export const ButtonGroup = styled.div<{
  $direction?: "vertical" | "horizontal";
  $align?: "flex-start" | "flex-end" | "center" | "stretch";
}>`
  display: flex;
  flex-direction: ${({ $direction }) => ($direction === "vertical" ? "column" : "row")};
  justify-content: ${({ $align }) => ($align ? $align : "center")};
  align-items: center;
  > div {
    padding: ${({ $direction }) => ($direction === "vertical" ? "5px 0" : "0 5px")};
  }
  > button {
    &:not(:last-of-type) {
      margin-right: 8px;
    }
    &:not(:first-of-type) {
      margin-left: 8px;
    }
  }
`;
